import { ArrowRight, ShieldCheck } from 'lucide-react';
import { C } from '../constants';
import Glass from '../components/Glass';
import ShareButton from '../components/ShareButton';

export default function LandingScreen({ stateData, onStart }) {
  const { name, nameLocal, localFont, accent, strings } = stateData;

  const steps = [
    { n: '01', en: 'Pick your district',  local: strings.step1Local, d: `All ${stateData.districts.length} districts of ${name} — pick yours.`, c: accent },
    { n: '02', en: 'Choose a party',      local: strings.step2Local, d: `${stateData.parties.length} parties on the ballot. Pick freely, no pressure.`, c: C.pink },
    { n: '03', en: 'See the state shift', local: strings.step3Local, d: 'Live map updates as votes pour in across the state.',  c: accent },
  ];

  return (
    <main className="px-5 md:px-10 py-12 md:py-20 max-w-5xl mx-auto">
      <h1 style={{
        fontFamily:    "'Poppins', sans-serif",
        fontWeight:    700,
        fontSize:      'clamp(2.4rem, 6.5vw, 5rem)',
        lineHeight:    1.05,
        letterSpacing: '-0.025em',
        marginTop:     '1.4rem',
        color:         C.white,
      }}>
        Who would you vote for —
        <br />
        <span style={{
          background:          `linear-gradient(90deg, ${accent}, ${C.pink})`,
          WebkitBackgroundClip:'text',
          backgroundClip:      'text',
          color:               'transparent',
          fontStyle:           'italic',
        }}>
          without any freebies?
        </span>
      </h1>

      <p style={{
        fontFamily: localFont,
        fontSize:   '1.15rem',
        color:      C.textDim,
        marginTop:  '1rem',
        maxWidth:   640,
        lineHeight: 1.45,
      }}>
        {strings.voteQuestion}
      </p>

      <div className="mt-10 flex items-center gap-4 flex-wrap">
        <ShareButton stateId={stateData.id} stateName={name} accent={accent} />
        <button
          onClick={onStart}
          className="inline-flex items-center gap-3"
          style={{
            background:    '#D32F2F',
            color:         '#ffffff',
            padding:       '0.85rem 2rem',
            fontWeight:    500,
            fontSize:      '0.95rem',
            borderRadius:  20,
            border:        'none',
            cursor:        'pointer',
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            boxShadow:     '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
            transition:    'box-shadow 200ms ease, background 200ms ease',
            fontFamily:    "'Poppins', sans-serif",
          }}
          onMouseEnter={e => {
            e.currentTarget.style.background = '#C62828';
            e.currentTarget.style.boxShadow = '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.background = '#D32F2F';
            e.currentTarget.style.boxShadow = '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)';
          }}
          onMouseDown={e => {
            e.currentTarget.style.background = '#B71C1C';
            e.currentTarget.style.boxShadow = '0px 5px 5px -3px rgba(0,0,0,0.2), 0px 8px 10px 1px rgba(0,0,0,0.14), 0px 3px 14px 2px rgba(0,0,0,0.12)';
          }}
          onMouseUp={e => {
            e.currentTarget.style.background = '#C62828';
            e.currentTarget.style.boxShadow = '0px 2px 4px -1px rgba(0,0,0,0.2), 0px 4px 5px 0px rgba(0,0,0,0.14), 0px 1px 10px 0px rgba(0,0,0,0.12)';
          }}
        >
          Cast Your Vote Now
          <span style={{
            fontFamily:  localFont,
            fontSize:    '0.95rem',
            fontWeight:  500,
            opacity:     0.85,
            borderLeft:  '1px solid rgba(255,255,255,0.3)',
            paddingLeft: 12,
            marginLeft:  4,
          }}>
            {strings.castVote}
          </span>
          <ArrowRight size={18} />
        </button>
        <span style={{ fontSize: '0.85rem', color: C.textDim, fontStyle: 'italic' }}>
          results unlock after you vote
        </span>
      </div>

      {/* Anonymity explainer */}
      <Glass style={{ padding: '1.2rem 1.4rem', marginTop: '2.5rem' }}>
        <div className="flex items-start gap-3">
          <ShieldCheck size={22} color={accent} style={{ marginTop: 2, flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 700, color: C.white, fontSize: '1rem' }}>
              Completely anonymous ·{' '}
              <span style={{ fontFamily: localFont }}>{strings.anonymityNote}</span>
            </div>
            <p style={{ color: C.textDim, fontSize: '0.85rem', marginTop: '0.4rem', lineHeight: 1.5 }}>
              We don't ask for your name, phone, email, or any personal info. We only store your
              district and chosen party — nothing that points back to you. Vote freely.
            </p>
          </div>
        </div>
      </Glass>

      {/* Step cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
        {steps.map(s => (
          <Glass key={s.n} style={{ padding: '1.2rem' }}>
            <div style={{ fontWeight: 700, fontStyle: 'italic', fontSize: '1.8rem', color: s.c, lineHeight: 1, textShadow: `0 0 12px ${s.c}66` }}>
              {s.n}
            </div>
            <div style={{ fontWeight: 700, fontSize: '1.1rem', marginTop: '0.6rem', color: C.white }}>
              {s.en}
            </div>
            <div style={{ fontFamily: localFont, fontSize: '0.82rem', color: C.textDim, marginTop: 3 }}>
              {s.local}
            </div>
            <p style={{ fontSize: '0.86rem', color: C.textDim, marginTop: '0.6rem', lineHeight: 1.45 }}>
              {s.d}
            </p>
          </Glass>
        ))}
      </div>
    </main>
  );
}
